import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import NotificationBell from './NotificationBell';
import { useTheme, THEMES } from '../context/ThemeContext';
import { API_URL } from '../config/api';
import { hasPermission, clearPermissions } from '../utils/permissions';

/**
 * Header Component (Recruiter / Admin)
 * 
 * Layout: Logo → Nav tabs → spacer → Search → Bell → Theme → Profile avatar
 * Active nav tab doubles as page indicator (no separate title).
 */

export default function Header({ onAddClick, onLogout, onExport, onImport }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.user_type === 'admin';

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('candidates');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);
  const resultsRef = useRef(null);

  // Profile menu state
  const [profileOpen, setProfileOpen] = useState(false);

  // Close search results on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        searchRef.current && !searchRef.current.contains(e.target) &&
        resultsRef.current && !resultsRef.current.contains(e.target)
      ) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }
    const timer = setTimeout(() => performSearch(), 300);
    return () => clearTimeout(timer);
  }, [searchQuery, searchType]);

  const performSearch = async () => {
    if (!searchQuery.trim()) return;
    setIsSearching(true);
    try {
      const token = localStorage.getItem('authToken');
      if (searchType === 'candidates') {
        const response = await axios.get(
          `${API_URL}/candidate-archive/internal-archive?search=${encodeURIComponent(searchQuery)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSearchResults((response.data || []).slice(0, 8));
      } else {
        const response = await axios.get(`${API_URL}/pipelines`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const filtered = response.data
          .filter(p =>
            p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            p.firm_name?.toLowerCase().includes(searchQuery.toLowerCase())
          )
          .slice(0, 8);
        setSearchResults(filtered);
      }
      setShowResults(true);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleResultClick = (result) => {
    if (searchType === 'candidates') {
      // Open the candidate via the preview-panel overlay rather than the
      // raw /candidate/:id page (which is being retired). The candidates
      // table reads ?candidate=N and opens the side panel on top of it,
      // matching the extension's "Open in Banyak" behavior.
      navigate(`/candidates-expanded?candidate=${result.candidate_id}`);
    } else {
      navigate(`/search/${result.id}`);
    }
    setShowResults(false);
    setSearchQuery('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowResults(false);
      setSearchQuery('');
    } else if (e.key === 'Enter' && searchQuery.trim()) {
      if (searchType === 'candidates') {
        navigate(`/candidates-expanded?search=${encodeURIComponent(searchQuery)}`);
      } else {
        navigate(`/searches?search=${encodeURIComponent(searchQuery)}`);
      }
      setShowResults(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('userId');
    clearPermissions();
    window.location.href = '/login';
  };

  const isActive = (path) => {
    if (path === '/candidates-expanded') return location.pathname === '/' || location.pathname === '/candidates-expanded' || location.pathname.startsWith('/candidate/');
    if (path === '/firms') return location.pathname === '/firms' || location.pathname.startsWith('/firm/');
    if (path === '/searches') return location.pathname === '/searches' || location.pathname.startsWith('/search/');
    return location.pathname.startsWith(path);
  };

  const navLinkClass = (path) =>
    `px-2.5 py-1 text-xs font-medium rounded transition-colors ${
      isActive(path)
        ? 'text-emerald-700 bg-emerald-50 border border-emerald-200'
        : 'text-gray-500 hover:text-emerald-700 hover:bg-emerald-50'
    }`;

  const initials = (user.name || 'U').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  const isDark = theme === THEMES.dark;

  return (
    <header className="bg-white border-b sticky top-0 z-40 h-[44px]">
      <div className="max-w-full px-4 h-full flex items-center">
        
        {/* Left: Logo + Nav */}
        <div className="flex items-center gap-4 flex-shrink-0">
          <img
            src="/Logo-Symbol-Colored.svg"
            alt="Logo"
            className="w-6 h-6 flex-shrink-0 cursor-pointer"
            onClick={() => navigate('/')}
          />
          <nav className="flex items-center gap-0.5">
            <button onClick={() => navigate('/')} className={navLinkClass('/candidates-expanded')}>Candidates</button>
            <button onClick={() => navigate('/searches')} className={navLinkClass('/searches')}>Searches</button>
            <button onClick={() => navigate('/outreach')} className={navLinkClass('/outreach')}>Outreach</button>
            <button onClick={() => navigate('/pipelines')} className={navLinkClass('/pipelines')}>Pipelines</button>
            <button onClick={() => navigate('/firms')} className={navLinkClass('/firms')}>Firms</button>
            <button onClick={() => navigate('/tags')} className={navLinkClass('/tags')}>Tags</button>
            <button onClick={() => navigate('/opportunities')} className={navLinkClass('/opportunities')}>Opportunities</button>
            {isAdmin && <button onClick={() => navigate('/users')} className={navLinkClass('/users')}>Users</button>}
          </nav>
        </div>

        {/* Spacer — pushes everything below to the right */}
        <div className="flex-1 min-w-[40px]" />

        {/* Right cluster: Search + actions */}
        <div className="flex items-center gap-2">
          
          {/* Search */}
          <div className="relative w-64" ref={searchRef}>
            <div className="flex items-center bg-gray-50 border border-gray-200 rounded-lg overflow-hidden focus-within:ring-1 focus-within:ring-emerald-500 focus-within:border-emerald-500">
              <select
                value={searchType}
                onChange={(e) => { setSearchType(e.target.value); setSearchResults([]); }}
                className="bg-transparent text-[11px] text-gray-500 pl-2 pr-0.5 py-1 border-r border-gray-200 focus:outline-none cursor-pointer"
              >
                <option value="candidates">Candidates</option>
                <option value="searches">Searches</option>
              </select>
              <div className="flex-1 flex items-center">
                <svg className="w-3.5 h-3.5 text-gray-400 ml-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => searchQuery.trim() && setShowResults(true)}
                  onKeyDown={handleKeyDown}
                  placeholder="Search..."
                  className="flex-1 bg-transparent text-xs px-1.5 py-1.5 focus:outline-none min-w-0"
                />
                {isSearching && (
                  <div className="mr-1.5">
                    <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-gray-300 border-t-emerald-500"></div>
                  </div>
                )}
                {searchQuery && !isSearching && (
                  <button
                    onClick={() => { setSearchQuery(''); setSearchResults([]); setShowResults(false); }}
                    className="mr-1.5 text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            {/* Search Results Dropdown */}
            {showResults && searchQuery.trim() && searchResults.length > 0 && (
              <div ref={resultsRef} className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-[400px] overflow-auto">
                {searchType === 'candidates' ? (
                  <>
                    <div className="px-3 py-1.5 text-xs font-medium text-gray-500 border-b bg-gray-50">Candidates ({searchResults.length})</div>
                    {searchResults.map((c) => (
                      <button key={c.candidate_id} onClick={() => handleResultClick(c)} className="w-full px-3 py-2 text-left hover:bg-emerald-50 flex items-center gap-3 border-b border-gray-100 last:border-0">
                        {c.profile_picture_url ? (
                          <img src={c.profile_picture_url} alt="" className="w-7 h-7 rounded-full object-cover" />
                        ) : (
                          <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-xs font-medium">{c.name?.charAt(0)?.toUpperCase()}</div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">{c.name}</div>
                          <div className="text-xs text-gray-500 truncate">{c.current_title || 'No title'}{c.current_company && ` at ${c.current_company}`}</div>
                        </div>
                        <div className="flex items-center gap-1.5">
                          {c.linkedin && (
                            <a href={c.linkedin.startsWith('http') ? c.linkedin : `https://${c.linkedin}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-[#0A66C2] hover:text-[#004182]">
                              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
                            </a>
                          )}
                          {c.email && <span className="text-gray-400" title="Has email"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg></span>}
                        </div>
                      </button>
                    ))}
                    <button onClick={() => { navigate(`/candidates-expanded?search=${encodeURIComponent(searchQuery)}`); setShowResults(false); setSearchQuery(''); }} className="w-full px-3 py-2 text-xs text-emerald-700 hover:bg-emerald-50 text-center font-medium">View all results in Candidates →</button>
                  </>
                ) : (
                  <>
                    <div className="px-3 py-1.5 text-xs font-medium text-gray-500 border-b bg-gray-50">Searches ({searchResults.length})</div>
                    {searchResults.map((p) => (
                      <button key={p.id} onClick={() => handleResultClick(p)} className="w-full px-3 py-2 text-left hover:bg-emerald-50 flex items-center gap-3 border-b border-gray-100 last:border-0">
                        <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-green-100 text-green-600">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">{p.name}</div>
                          <div className="text-xs text-gray-500">{p.firm_name || 'No firm'} • {p.card_count ?? 0} candidates</div>
                        </div>
                      </button>
                    ))}
                    <button onClick={() => { navigate(`/searches?search=${encodeURIComponent(searchQuery)}`); setShowResults(false); setSearchQuery(''); }} className="w-full px-3 py-2 text-xs text-emerald-700 hover:bg-emerald-50 text-center font-medium">View all in Searches →</button>
                  </>
                )}
              </div>
            )}

            {showResults && searchQuery.trim() && searchResults.length === 0 && !isSearching && (
              <div ref={resultsRef} className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-50">
                <p className="text-sm text-gray-500 text-center">No {searchType === 'candidates' ? 'candidates' : 'searches'} found for "{searchQuery}"</p>
              </div>
            )}
          </div>

          {/* Add Firm (only on firms page) */}
          {location.pathname === '/firms' && onAddClick && (
            <button onClick={onAddClick} className="px-2.5 py-1 text-xs bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-1">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
              Add Firm
            </button>
          )}

          <NotificationBell />

          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDark ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
            )}
          </button>

          {/* Profile Avatar */}
          <div className="relative">
            <button
              onClick={() => setProfileOpen(!profileOpen)}
              className="w-7 h-7 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center text-white text-[11px] font-semibold hover:ring-2 hover:ring-emerald-300 transition-all flex-shrink-0"
              title={user.name || 'Profile'}
            >
              {initials}
            </button>
            {profileOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setProfileOpen(false)} />
                <div className="absolute right-0 mt-1.5 w-52 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1">
                  {/* User info */}
                  <div className="px-3 py-2 border-b">
                    <p className="text-sm font-medium text-gray-900 truncate">{user.name || 'User'}</p>
                    <p className="text-xs text-gray-500 truncate">{user.email || ''}</p>
                  </div>
                  <button onClick={() => { navigate('/profile'); setProfileOpen(false); }} className="w-full px-3 py-2 text-sm text-left hover:bg-gray-50 flex items-center gap-2 text-gray-700">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                    My Profile
                  </button>
                  {user.user_type !== 'client' && (
                    <button onClick={() => { navigate('/user-management'); setProfileOpen(false); }} className="w-full px-3 py-2 text-sm text-left hover:bg-gray-50 flex items-center gap-2 text-gray-700">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
                      User Management
                    </button>
                  )}
                  {onExport && hasPermission('export_data') && (
                    <button onClick={() => { onExport(); setProfileOpen(false); }} className="w-full px-3 py-2 text-sm text-left hover:bg-gray-50 flex items-center gap-2 text-gray-700">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                      Export Firms
                    </button>
                  )}
                  {onImport && hasPermission('import_candidates') && (
                    <button onClick={() => { onImport(); setProfileOpen(false); }} className="w-full px-3 py-2 text-sm text-left hover:bg-gray-50 flex items-center gap-2 text-gray-700">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                      Import Firms
                    </button>
                  )}
                  <div className="border-t my-1"></div>
                  <button onClick={() => { handleLogout(); setProfileOpen(false); }} className="w-full px-3 py-2 text-sm text-left text-red-600 hover:bg-red-50 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                    Sign Out
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
